import { useEffect, useMemo, useState } from "react";

export type Mastery = "known" | "fuzzy" | "new";

export type ProgressState = {
  mastery: Record<string, Mastery>;
  mistakes: string[];
  studyLog: Record<string, number>;
  dailyGoal: number;
};

const STORAGE_KEY = "shiyu-progress-v1";

const emptyProgress: ProgressState = {
  mastery: {},
  mistakes: [],
  studyLog: {},
  dailyGoal: 10
};

const localDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const readProgress = (): ProgressState => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return emptyProgress;
    const parsed = JSON.parse(stored) as Partial<ProgressState>;
    return {
      mastery: parsed.mastery ?? {},
      mistakes: parsed.mistakes ?? [],
      studyLog: parsed.studyLog ?? {},
      dailyGoal: parsed.dailyGoal ?? 10
    };
  } catch {
    return emptyProgress;
  }
};

export const useProgress = () => {
  const [progress, setProgress] = useState<ProgressState>(readProgress);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  }, [progress]);

  const setMastery = (id: string, mastery: Mastery) => {
    setProgress((current) => ({
      ...current,
      mastery: { ...current.mastery, [id]: mastery },
      mistakes:
        mastery === "known"
          ? current.mistakes.filter((birdId) => birdId !== id)
          : current.mistakes
    }));
  };

  const markStudied = () => {
    const today = localDateKey();
    setProgress((current) => ({
      ...current,
      studyLog: {
        ...current.studyLog,
        [today]: (current.studyLog[today] ?? 0) + 1
      }
    }));
  };

  const recordQuizAnswer = (id: string, correct: boolean) => {
    setProgress((current) => ({
      ...current,
      mastery: correct
        ? current.mastery
        : { ...current.mastery, [id]: "fuzzy" },
      mistakes: correct
        ? current.mistakes
        : Array.from(new Set([...current.mistakes, id]))
    }));
  };

  const clearMistake = (id: string) => {
    setProgress((current) => ({
      ...current,
      mistakes: current.mistakes.filter((birdId) => birdId !== id)
    }));
  };

  const resetProgress = () => setProgress(emptyProgress);

  const stats = useMemo(() => {
    const known = Object.values(progress.mastery).filter(
      (value) => value === "known"
    ).length;
    const fuzzy = Object.values(progress.mastery).filter(
      (value) => value === "fuzzy"
    ).length;
    const learning = Object.values(progress.mastery).filter(
      (value) => value === "new"
    ).length;
    const today = progress.studyLog[localDateKey()] ?? 0;

    let streak = 0;
    const cursor = new Date();
    if (!progress.studyLog[localDateKey(cursor)]) {
      cursor.setDate(cursor.getDate() - 1);
    }
    while (progress.studyLog[localDateKey(cursor)]) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }

    return { known, fuzzy, learning, today, streak };
  }, [progress]);

  return {
    progress,
    stats,
    setMastery,
    markStudied,
    recordQuizAnswer,
    clearMistake,
    resetProgress
  };
};
